import type { SystemListBand } from 'app/types/navigation'
import { ACTION_QUEUE_BY_ID, ActionQueueDef, QueueSearchOpts, hygieneWindow } from './queueRegistry'

/**
 * Hygiene recall, split by how the due date sits against today.
 *
 * Unlike treatment's four bands (one array driving four derived queues, see
 * treatmentBands.ts), the three hygiene windows are already three independent
 * `ActionQueueDef`s in queueRegistry.ts, each with its own sort and its own
 * hand-tuned copy. A "band" here is not a second definition of those windows;
 * it is a choice of which registered queue's search is showing, so a band id
 * is just that queue's id, and resolving a band to a search means asking the
 * registry for it rather than repeating it.
 */
export type HygieneBandId = `hygiene-coming-due` | `due-for-hygiene` | `hygiene-long-past-due`

/** A window somebody typed a day count for: `due:14` is due within 14 days, `overdue:60` over 60 days overdue. */
type HygieneDayWindow = `due:${number}` | `overdue:${number}`

export type HygieneBand = HygieneBandId | HygieneDayWindow

const DAY_WINDOW = /^(due|overdue):([1-9]\d{0,3})$/

/**
 * Soonest due to most overdue: the order a front desk actually thinks in,
 * book the easy ones before they lapse, then work backward through how late
 * everyone else is. Matches the day-offset order the underlying windows sit
 * in (see `hygieneBandSearchTerms`'s doc comment).
 */
const HYGIENE_BAND_IDS: HygieneBandId[] = [`hygiene-coming-due`, `due-for-hygiene`, `hygiene-long-past-due`]

function hygieneBandQueue(id: HygieneBand): ActionQueueDef {
  const dayWindow = DAY_WINDOW.exec(id)
  if (dayWindow) {
    const days = Number(dayWindow[2])
    const due = dayWindow[1] === `due`
    // Read in the order of the preset window on the same side of today.
    const anchor = hygieneBandQueue(due ? `hygiene-coming-due` : `hygiene-long-past-due`)
    return {
      ...anchor,
      shortName: due ? `Due within ${days} days` : `Over ${days} days overdue`,
      searchTerms: (opts) => (due ? hygieneWindow(1, days, opts) : hygieneWindow(-3650, -(days + 1), opts)),
    }
  }
  const def = ACTION_QUEUE_BY_ID.get(id)
  // The ids come from a closed union that names three registry entries by
  // hand, so a miss here is a wiring mistake, not bad input.
  if (!def) throw new Error(`Unknown hygiene band queue: ${id}`)
  return def
}

/** One option in the board's band control. `id` undefined is every window at once. */
export type HygieneBandOption = { id: HygieneBandId | undefined; label: string }

/**
 * The Hygiene Recall board's band control, the whole cohort first.
 *
 * It leads for the same reason treatment's does: clicking into a pipeline
 * should show the pipeline, not one slice of it pre-narrowed. "Any time"
 * rather than "All", matching treatment: the control is a menu whose trigger
 * shows only the chosen label, and a button reading "All" beside a locations
 * filter says nothing about what it is all of. Labels
 * come from each queue's own `shortName` rather than being written out again
 * here, so the control and the Action Center's own hygiene bundle card can
 * never drift apart on what a window is called.
 */
export const HYGIENE_BAND_OPTIONS: HygieneBandOption[] = [
  { id: undefined, label: `Any time` },
  ...HYGIENE_BAND_IDS.map((id) => {
    const def = hygieneBandQueue(id)
    return { id, label: def.shortName ?? def.name }
  }),
]

/**
 * The band a `?band=` URL value names, or `undefined` ("All") when it does
 * not name one.
 *
 * Same contract as `treatmentBands.ts`'s `bandFromParam`: a missing or
 * unrecognised value both read as "All" rather than as an error, since the
 * param is user-editable (typed, bookmarked, or shared from an older build).
 */
export function hygieneBandFromParam(value: string | null): HygieneBandId | undefined {
  return HYGIENE_BAND_IDS.some((id) => id === value) ? (value as HygieneBandId) : undefined
}

export function hygieneDayWindowFromParam(value: string | null): HygieneDayWindow | undefined {
  return value !== null && DAY_WINDOW.test(value) ? (value as HygieneDayWindow) : undefined
}

/** A typed day window as a Hygiene Recall band, for the list's table and board. */
export function hygieneDayWindowBand(param: string): SystemListBand | undefined {
  const dayWindow = hygieneDayWindowFromParam(param)
  if (!dayWindow) return undefined
  const queue = hygieneBandQueue(dayWindow)
  return { id: dayWindow, label: queue.shortName ?? queue.name, queue }
}

/**
 * The band the board opens on when the URL names none.
 *
 * Coming Due, not All. Clicking Hygiene Recall in the nav is somebody asking
 * "who do I need to call about a cleaning", and the answer worth putting
 * first is the people about to lapse: they are the cheapest to save, and a
 * board that opens on every patient going back ten years is a list rather
 * than a day's work. Every other window is one click away.
 */
export const DEFAULT_HYGIENE_BAND: HygieneBandId = `hygiene-coming-due`

/**
 * The band a `?band=` value names, falling back to `DEFAULT_HYGIENE_BAND`
 * when the URL carries no band at all.
 *
 * Three cases, deliberately not two, the same shape as treatment's. A
 * **missing** param means nobody has chosen yet (the pipeline nav, a bare
 * link), so the default applies. The literal `all` means somebody chose the
 * whole cohort and has to survive a reload, which is why the control writes
 * it rather than stripping the param. A value this build does not recognise
 * widens to "All": showing everyone beats showing nobody.
 */
export function hygieneBandFromParamOrDefault(value: string | null): HygieneBandId | undefined {
  if (value === null) return DEFAULT_HYGIENE_BAND
  return hygieneBandFromParam(value)
}

/**
 * The search for one band, or for "All" the union of the three.
 *
 * A band's own search is never redefined here: it is the queue definition's
 * `searchTerms`, the exact same query the Action Center's own queue card
 * already runs, so the board and the card can never disagree on a count.
 *
 * The union is expressed as one `hygieneWindow` call rather than as the three
 * queues' searches OR'd together, because the three ranges turn out to tile
 * the due-date axis exactly: `hygiene-long-past-due` is -3650 to -31 days
 * from today, `due-for-hygiene` is -30 to 0, and `hygiene-coming-due` (built
 * by hand in queueRegistry.ts rather than through `hygieneWindow`, but the
 * same shape) is +1 to +30. Neither a gap nor an overlap sits between them,
 * so `hygieneWindow(-3650, 30)` covers precisely what the three cover
 * together, through the same patientStatus/appointmentStatus/birthDate/isPerio
 * filters every one of them already applies.
 */
export function hygieneBandSearchTerms(id: HygieneBand | undefined, opts: QueueSearchOpts): string[] {
  if (id) return hygieneBandQueue(id).searchTerms(opts)
  return hygieneWindow(-3650, 30, opts)
}

/**
 * The order a band is read in, from the band's own queue.
 *
 * Each of the three windows already declares the order that makes sense for
 * it, and they are not the same order: soonest first while a cleaning is
 * still ahead, because the point is catching somebody before they lapse; most
 * recently due first once it has passed, because those are the easiest to win
 * back. The board was ignoring all of that and sorting every band by the
 * bundle's own default, so "Due within 30 days" opened on the people due in
 * 29 days and buried the ones due tomorrow.
 *
 * "All" keeps the bundle's order: it spans every window, and no one window's
 * reasoning covers the whole span.
 */
export function hygieneBandSort(
  id: HygieneBand | undefined,
  opts: QueueSearchOpts,
): { field: string; direction: `ASC` | `DESC` } | undefined {
  return id ? hygieneBandQueue(id).sort(opts) : undefined
}

export type HygieneSortId = `longest` | `recent`

/**
 * Which end of the recall axis comes first.
 *
 * A direction toggle rather than a choice of field: all three windows already
 * order on the same `lastHygieneDate` and differ only in which way round.
 *
 * The labels name the field, not the direction. "Oldest" and "Newest" would
 * beg the question of what is old, and the honest answer changes meaning
 * across the axis: the same ascending order is "due soonest" for a cleaning
 * still ahead and "most overdue" for one long past. Naming the visit instead
 * reads the same way in every window.
 */
export const HYGIENE_SORTS: { id: HygieneSortId; label: string; shortLabel: string }[] = [
  { id: `longest`, label: `Longest since a visit`, shortLabel: `Longest` },
  { id: `recent`, label: `Most recent visit`, shortLabel: `Recent` },
]

/**
 * The order to read a window in, and which option the control should show for it.
 *
 * `chosen` is what somebody picked, if anything. With nothing picked the
 * window's own declared order wins: soonest first while a cleaning is still
 * ahead, most recently due once it has passed (see `hygieneBandSort`). That
 * tuning is per window, and a board ignoring it is what once opened "Due
 * within 30 days" on the people due in 29 days while burying the ones due
 * tomorrow. So a picked order is deliberately not carried from one window to
 * the next: the caller drops it when the band changes, and every window opens
 * on the order that suits it.
 *
 * `anchor` covers "Any time", which spans every window and so declares no
 * order of its own. It falls back to the queue the board is mounted on rather
 * than to a direction written out again here.
 */
export function hygieneSort(
  band: HygieneBand | undefined,
  opts: QueueSearchOpts,
  anchor: ActionQueueDef,
  chosen?: HygieneSortId,
): { spec: { field: string; direction: `ASC` | `DESC` }; current: HygieneSortId } {
  const declared = hygieneBandSort(band, opts) ?? anchor.sort(opts)
  const current = chosen ?? (declared.direction === `ASC` ? `longest` : `recent`)
  return { spec: { field: declared.field, direction: current === `longest` ? `ASC` : `DESC` }, current }
}
